import { Container, Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';
import { WALKABLE_TILES } from '../systems/DungeonGenerator.js';

const DEFAULT_COLOR = 0x44aaff;
const DODGE_ALPHA = 0.4;

/**
 * Player entity with movement, collision, dodge roll, facing direction, melee attack,
 * and class-based stats/visuals (M11).
 */
export class Player {
  constructor(x, y, classData) {
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
    this.size = 14; // slightly smaller than tile for clearance

    // Class data (M11)
    this.classId = classData ? classData._id : 'ironclad';
    this.classData = classData || null;

    // Apply class stats or use defaults
    const stats = classData ? classData.stats : null;
    this.maxHp = stats ? stats.maxHp : 100;
    this.hp = this.maxHp;
    this.speed = stats ? stats.speed : 80;
    this.damageMult = stats ? stats.damage : 1.0;
    this.defense = stats ? stats.defense : 0.0;

    // Resolve class color
    this.playerColor = DEFAULT_COLOR;
    if (classData && classData.color) {
      this.playerColor = parseInt(classData.color, 16);
    }

    this.isDodging = false;
    this.dodgeTimer = 0;
    this.dodgeCooldown = 0;
    this.dodgeSpeed = 200;
    this.dodgeDuration = 0.25; // seconds
    this.dodgeCooldownTime = 0.6;
    this.facingX = 0;
    this.facingY = 1; // facing down by default

    // Ability shield (M12 - Voidpriest's Radiant Ward)
    this.shield = 0;
    this.stunImmune = false; // for future use, abilities can't be interrupted

    // Ability readiness indicator (M12)
    this.abilityReady = false; // set by GameplayScene from AbilitySystem
    this.abilityIndicatorGfx = null;

    // Player glow effect (M12 - ability visuals)
    this.glowTimer = 0;
    this.glowColor = 0xffffff;

    // Equipment slots (M13)
    this.equipment = {
      weapon: null,   // weapon item data
      body: null,     // armor
      ring: null,     // accessory ring
      amulet: null    // accessory amulet
    };

    // Weapon & inventory (M9, expanded M14)
    this.weapon = null;       // current weapon data object (from items.json)
    this.weaponId = null;     // current weapon key
    this.inventory = [];      // array of { itemId, itemData } (max 15, any type)
    this.maxInventory = 15;

    // Attack state
    this.isAttacking = false;
    this.attackTimer = 0;
    this.attackCooldown = 0;
    this.attackDuration = 0.2;    // active hitbox time
    this.attackCooldownTime = 0.35;

    // Hit invulnerability (M7) -- separate from dodge invuln
    this.hitInvulnTimer = 0;
    this.hitInvulnDuration = 0.5; // seconds of invuln after being hit
    this.hitFlashTimer = 0;
    this.hitFlashDuration = 0.3;  // seconds of red flash

    // Knockback from being hit
    this.hitKnockbackX = 0;
    this.hitKnockbackY = 0;

    // Status effects (M18)
    this.statusEffects = new Map();

    // Relic system (M21)
    this.relics = []; // max 4 equipped relics (managed by RelicSystem)

    // Stat allocations reference (M22) -- set from GameState by GameplayScene
    this.statAllocations = { maxHp: 0, damage: 0, speed: 0, defense: 0 };

    // Relic modifiers reference (M21) -- set by GameplayScene from RelicSystem
    this.relicModifiers = null;

    // Ranged class support (M19)
    this.isRangedClass = classData ? (classData.ranged === true) : false;
    this.projectileType = classData ? (classData.projectileType || null) : null;

    // Callback for when player takes damage (used by GameplayScene for screen effects)
    this.onDamaged = null;

    this.container = new Container();
    this.gfx = null;
    this.slashGfx = null;
    this.createGraphics();
  }

  /**
   * Equip a weapon, updating attack stats from weapon data.
   * @param {object} weaponData - Weapon data from items.json
   * @param {string} weaponId - Weapon key string
   */
  equipWeapon(weaponData, weaponId) {
    this.weapon = weaponData;
    this.weaponId = weaponId;

    // Apply weapon stats to attack properties
    if (weaponData.speed !== undefined) {
      this.attackCooldownTime = weaponData.speed;
    }
  }

  /**
   * Get the damage range for the current weapon (or default), multiplied by damageMult.
   * @returns {number[]} [min, max] damage
   */
  getDamageRange() {
    let base;
    if (this.weapon && this.weapon.damage) {
      base = this.weapon.damage;
    } else {
      base = [10, 15]; // default (no weapon)
    }
    return [
      Math.round(base[0] * this.damageMult),
      Math.round(base[1] * this.damageMult)
    ];
  }

  /**
   * Get the attack range for the current weapon (or default).
   * @returns {number} attack range in pixels
   */
  getAttackRange() {
    if (this.weapon && this.weapon.range) {
      return this.weapon.range;
    }
    return 20; // default
  }

  /**
   * Use a consumable from inventory at the given index.
   * @param {number} index - Inventory slot index (0-4)
   * @returns {object|null} The consumed item data, or null if slot is empty
   */
  useConsumable(index) {
    if (index < 0 || index >= this.inventory.length) return null;

    const item = this.inventory[index];
    if (!item) return null;

    const data = item.itemData;

    // Apply effect based on type
    if (data.type === 'consumable' && data.healAmount) {
      const oldHp = this.hp;
      this.hp = Math.min(this.maxHp, this.hp + data.healAmount);
      const healed = this.hp - oldHp;
      // Remove from inventory
      this.inventory.splice(index, 1);
      return { ...data, healed };
    }

    return null;
  }

  /**
   * Add an item to the backpack inventory.
   * @param {object} item - { itemId, itemData }
   * @returns {boolean} True if added successfully, false if inventory full
   */
  addToInventory(item) {
    if (this.inventory.length >= this.maxInventory) return false;
    this.inventory.push(item);
    return true;
  }

  /**
   * Equip an item to the given slot. Returns the previously equipped item (or null).
   * @param {string} slot - 'weapon', 'body', 'ring', or 'amulet'
   * @param {object} itemData - The item data object
   * @param {string} itemId - The item key string
   * @returns {object|null} Previously equipped item as { itemId, itemData }, or null
   */
  equipItem(slot, itemData, itemId) {
    const prev = this.equipment[slot];
    let prevItem = null;

    if (prev) {
      prevItem = { itemId: prev.itemId, itemData: prev.itemData };
    }

    this.equipment[slot] = { itemId, itemData };

    // If equipping a weapon, also update legacy weapon fields
    if (slot === 'weapon') {
      this.equipWeapon(itemData, itemId);
    }

    // Recalculate effective stats
    this._applyEffectiveStats();

    return prevItem;
  }

  /**
   * Unequip an item from the given slot. Returns the removed item or null.
   * @param {string} slot - 'weapon', 'body', 'ring', or 'amulet'
   * @returns {object|null} The removed item as { itemId, itemData }, or null
   */
  unequipItem(slot) {
    const prev = this.equipment[slot];
    if (!prev) return null;

    const prevItem = { itemId: prev.itemId, itemData: prev.itemData };
    this.equipment[slot] = null;

    // If unequipping a weapon, clear legacy weapon fields
    if (slot === 'weapon') {
      this.weapon = null;
      this.weaponId = null;
      this.attackCooldownTime = 0.35; // default
    }

    // Recalculate effective stats
    this._applyEffectiveStats();

    return prevItem;
  }

  /**
   * Calculate and apply effective stats from base class + all equipment.
   * Updates defense, speed, maxHp, damageMult based on equipment bonuses.
   */
  getEffectiveStats() {
    const stats = this.classData ? this.classData.stats : null;
    const baseDefense = stats ? stats.defense : 0.0;
    const baseSpeed = stats ? stats.speed : 80;
    const baseMaxHp = stats ? stats.maxHp : 100;
    const baseDamage = stats ? stats.damage : 1.0;

    let totalDefense = baseDefense;
    let totalSpeed = baseSpeed;
    let totalMaxHp = baseMaxHp;
    let totalDamageMod = baseDamage;
    let totalCritChance = 0;

    // Armor bonuses
    const body = this.equipment.body;
    if (body && body.itemData) {
      totalDefense += body.itemData.defense || 0;
      totalSpeed += body.itemData.speedMod || 0;
    }

    // Ring bonuses
    const ring = this.equipment.ring;
    if (ring && ring.itemData) {
      totalMaxHp += ring.itemData.hpBonus || 0;
      totalDamageMod += ring.itemData.damageMod || 0;
      totalDefense += ring.itemData.defense || 0;
      totalCritChance += ring.itemData.critChance || 0;
    }

    // Amulet bonuses
    const amulet = this.equipment.amulet;
    if (amulet && amulet.itemData) {
      totalMaxHp += amulet.itemData.hpBonus || 0;
      totalDamageMod += amulet.itemData.damageMod || 0;
      totalDefense += amulet.itemData.defense || 0;
      totalCritChance += amulet.itemData.critChance || 0;
    }

    // Stat allocations from level-up (M22)
    const allocs = this.statAllocations || { maxHp: 0, damage: 0, speed: 0, defense: 0 };
    totalMaxHp += allocs.maxHp * 5;           // +5 HP per point
    totalDamageMod += allocs.damage * 0.05;   // +5% damage per point
    totalSpeed += allocs.speed * 3;           // +3 speed per point
    totalDefense += allocs.defense * 0.02;    // +2% defense per point

    // Relic modifiers (M21)
    if (this.relicModifiers) {
      const rm = this.relicModifiers;
      totalDamageMod += rm.damageMod || 0;
      totalMaxHp = Math.round(totalMaxHp * (1 + (rm.hpMod || 0)));
      totalSpeed = Math.round(totalSpeed * (1 + (rm.moveMod || 0)));
      totalDefense += rm.defenseMod || 0;
      totalCritChance += rm.critChance || 0;
    }

    // Meta-progression unlock bonuses (M27)
    if (this.unlockBonuses) {
      const ub = this.unlockBonuses;
      totalMaxHp += ub.maxHp || 0;
      totalDamageMod += ub.damage || 0;
      totalDefense += ub.defense || 0;
    }

    return {
      defense: totalDefense,
      speed: totalSpeed,
      maxHp: totalMaxHp,
      damageMod: totalDamageMod,
      critChance: totalCritChance,
    };
  }

  /**
   * Apply effective stats from getEffectiveStats() to the player properties.
   */
  _applyEffectiveStats() {
    const eff = this.getEffectiveStats();
    this.defense = eff.defense;
    this.speed = eff.speed;

    // Update maxHp; if current hp exceeds new max, clamp it
    const oldMaxHp = this.maxHp;
    this.maxHp = eff.maxHp;
    if (this.hp > this.maxHp) {
      this.hp = this.maxHp;
    }
    // If maxHp increased, give the extra HP
    if (this.maxHp > oldMaxHp) {
      this.hp += (this.maxHp - oldMaxHp);
    }

    this.damageMult = eff.damageMod;
  }

  /**
   * Draw the player sprite with class-specific shape and color.
   * Offset is centered: the container position is the player's top-left,
   * but we draw centered within a TILE_SIZE area so camera math stays simple.
   */
  createGraphics() {
    this.gfx = new Graphics();
    const offset = (TILE_SIZE - this.size) / 2;
    const cx = TILE_SIZE / 2;
    const cy = TILE_SIZE / 2;
    const half = this.size / 2;

    if (this.classId === 'riftmage') {
      // Diamond (rotated square)
      this.gfx.moveTo(cx, cy - half);
      this.gfx.lineTo(cx + half, cy);
      this.gfx.lineTo(cx, cy + half);
      this.gfx.lineTo(cx - half, cy);
      this.gfx.closePath();
      this.gfx.fill(this.playerColor);
    } else if (this.classId === 'phantom') {
      // Triangle pointing down (default facing)
      this.gfx.moveTo(cx, cy + half);
      this.gfx.lineTo(cx - half, cy - half);
      this.gfx.lineTo(cx + half, cy - half);
      this.gfx.closePath();
      this.gfx.fill(this.playerColor);
    } else if (this.classId === 'voidpriest') {
      // Circle
      this.gfx.circle(cx, cy, half);
      this.gfx.fill(this.playerColor);
    } else {
      // Ironclad / default: square
      this.gfx.rect(offset, offset, this.size, this.size);
      this.gfx.fill(this.playerColor);
    }

    this.container.addChild(this.gfx);

    // Ability readiness indicator dot (M12)
    this.abilityIndicatorGfx = new Graphics();
    this.abilityIndicatorGfx.circle(TILE_SIZE / 2 + half + 2, TILE_SIZE / 2 - half - 2, 2);
    this.abilityIndicatorGfx.fill(0x888888);
    this.abilityIndicatorGfx.visible = false;
    this.container.addChild(this.abilityIndicatorGfx);

    // Shield visual ring (M12 - Voidpriest ward)
    this.shieldGfx = new Graphics();
    this.shieldGfx.visible = false;
    this.container.addChild(this.shieldGfx);

    // Glow overlay (M12 - ability visuals)
    this.glowGfx = new Graphics();
    this.glowGfx.visible = false;
    this.container.addChild(this.glowGfx);

    // Slash visual (hidden by default)
    this.slashGfx = new Graphics();
    this.slashGfx.visible = false;
    this.container.addChild(this.slashGfx);
  }

  /**
   * Begin a melee attack if not on cooldown.
   * @returns {boolean} True if attack started
   */
  startAttack() {
    if (this.isAttacking || this.attackCooldown > 0 || this.isDodging) return false;

    this.isAttacking = true;
    this.attackTimer = this.attackDuration;
    this.attackCooldown = this.attackCooldownTime;

    // Draw slash visual
    this._drawSlash();

    return true;
  }

  /**
   * Draw the attack slash line in facing direction.
   */
  _drawSlash() {
    this.slashGfx.clear();

    const cx = TILE_SIZE / 2;
    const cy = TILE_SIZE / 2;

    // Normalize facing direction
    const fx = this.facingX;
    const fy = this.facingY;
    const len = Math.sqrt(fx * fx + fy * fy);
    if (len === 0) return;
    const nx = fx / len;
    const ny = fy / len;

    // Slash start and end points (perpendicular to facing, offset forward)
    const forwardDist = 10;
    const slashLen = 10;
    const baseX = cx + nx * forwardDist;
    const baseY = cy + ny * forwardDist;

    // Perpendicular direction
    const px = -ny;
    const py = nx;

    const x1 = baseX + px * slashLen;
    const y1 = baseY + py * slashLen;
    const x2 = baseX - px * slashLen;
    const y2 = baseY - py * slashLen;

    this.slashGfx.moveTo(x1, y1);
    this.slashGfx.lineTo(x2, y2);
    this.slashGfx.stroke({ width: 2, color: 0xffffff });
    this.slashGfx.visible = true;
  }

  /**
   * Main update: handles input, dodge roll, attack, movement, and collision.
   * @param {number} dt - Delta time in seconds
   * @param {object} input - InputManager instance
   * @param {number[][]} tiles - 2D tile grid from dungeon
   */
  update(dt, input, tiles) {
    // Tick dodge cooldown
    if (this.dodgeCooldown > 0) {
      this.dodgeCooldown -= dt;
    }

    // Tick attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= dt;
    }

    // Tick hit invulnerability
    if (this.hitInvulnTimer > 0) {
      this.hitInvulnTimer -= dt;
      if (this.hitInvulnTimer <= 0) {
        this.hitInvulnTimer = 0;
      }
    }

    // Tick hit flash (red tint)
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= dt;
      if (this.hitFlashTimer <= 0) {
        this.hitFlashTimer = 0;
        this.gfx.tint = 0xffffff; // restore original tint
      }
    }

    // Apply hit knockback decay
    if (this.hitKnockbackX !== 0 || this.hitKnockbackY !== 0) {
      const kbX = this.hitKnockbackX * dt * 4;
      const kbY = this.hitKnockbackY * dt * 4;
      this._applyMovement(kbX, kbY, tiles);
      this.hitKnockbackX *= (1 - dt * 10);
      this.hitKnockbackY *= (1 - dt * 10);
      // Zero out when negligible
      if (Math.abs(this.hitKnockbackX) < 0.5) this.hitKnockbackX = 0;
      if (Math.abs(this.hitKnockbackY) < 0.5) this.hitKnockbackY = 0;
    }

    // Handle active attack timer
    if (this.isAttacking) {
      this.attackTimer -= dt;
      if (this.attackTimer <= 0) {
        this.isAttacking = false;
        this.attackTimer = 0;
        this.slashGfx.visible = false;
      } else {
        // Fade out the slash visual
        this.slashGfx.alpha = this.attackTimer / this.attackDuration;
      }
    }

    // Handle active dodge roll
    if (this.isDodging) {
      this.dodgeTimer -= dt;
      if (this.dodgeTimer <= 0) {
        this.isDodging = false;
        this.dodgeTimer = 0;
        this.gfx.alpha = 1.0;
      } else {
        // Move in dodge direction
        const moveX = this.facingX * this.dodgeSpeed * dt;
        const moveY = this.facingY * this.dodgeSpeed * dt;
        this._applyMovement(moveX, moveY, tiles);
        this._syncVisual();
        return; // No normal input during dodge
      }
    }

    // Read movement input (WASD)
    let dx = 0;
    let dy = 0;
    if (input.isKeyDown('KeyA')) dx -= 1;
    if (input.isKeyDown('KeyD')) dx += 1;
    if (input.isKeyDown('KeyW')) dy -= 1;
    if (input.isKeyDown('KeyS')) dy += 1;

    // Normalize diagonal movement
    if (dx !== 0 && dy !== 0) {
      const inv = 1 / Math.SQRT2;
      dx *= inv;
      dy *= inv;
    }

    // Update facing direction (only when there is movement input)
    if (dx !== 0 || dy !== 0) {
      this.facingX = dx;
      this.facingY = dy;
    }

    // Check for dodge roll input (Space)
    if (input.isKeyJustPressed('Space')) {
      this.startDodge();
    }

    // Apply speed modifier from status effects (M18)
    let effectiveSpeed = this.speed;
    if (this.statusEffects && this.statusEffects.size > 0) {
      if (this.statusEffects.has('frozen')) {
        effectiveSpeed *= 0.4; // 60% slow
      }
      if (this.statusEffects.has('stunned')) {
        effectiveSpeed = 0;
      }
    }

    // Apply normal movement with collision
    const moveX = dx * effectiveSpeed * dt;
    const moveY = dy * effectiveSpeed * dt;
    this._applyMovement(moveX, moveY, tiles);

    // Update ability indicator (M12)
    this._updateAbilityIndicator();

    // Update shield visual (M12)
    this._updateShieldVisual();

    // Update glow effect (M12)
    if (this.glowTimer > 0) {
      this.glowTimer -= dt;
      if (this.glowTimer <= 0) {
        this.glowTimer = 0;
        this.glowGfx.visible = false;
      } else {
        this.glowGfx.visible = true;
        this.glowGfx.alpha = Math.min(1.0, this.glowTimer * 2) * 0.3;
      }
    }

    this._syncVisual();
  }

  /**
   * Initiate a dodge roll in the current facing direction.
   */
  startDodge() {
    if (this.isDodging || this.dodgeCooldown > 0) return;

    // Normalize facing direction for dodge
    const len = Math.sqrt(this.facingX * this.facingX + this.facingY * this.facingY);
    if (len === 0) return; // no facing direction, cannot dodge

    this.facingX /= len;
    this.facingY /= len;

    this.isDodging = true;
    this.dodgeTimer = this.dodgeDuration;
    this.dodgeCooldown = this.dodgeCooldownTime;
    this.gfx.alpha = DODGE_ALPHA;
  }

  /**
   * Returns true if the player is currently invulnerable (during dodge OR hit invuln).
   */
  isInvulnerable() {
    return this.isDodging || this.hitInvulnTimer > 0;
  }

  /**
   * Apply damage to the player. Respects invulnerability and defense.
   * Triggers red flash, knockback, and hit invulnerability window.
   * @param {number} amount - Damage amount
   * @param {number} [fromX] - X position of the damage source (for knockback direction)
   * @param {number} [fromY] - Y position of the damage source (for knockback direction)
   */
  takeDamage(amount, fromX, fromY) {
    if (this.isInvulnerable()) return;

    // Apply defense reduction (M11)
    const reduced = Math.round(amount * (1 - this.defense));
    let finalDmg = Math.max(1, reduced); // always take at least 1 damage

    // Shield absorbs damage first (M12 - Voidpriest ward)
    if (this.shield > 0) {
      if (this.shield >= finalDmg) {
        this.shield -= finalDmg;
        finalDmg = 0;
      } else {
        finalDmg -= this.shield;
        this.shield = 0;
      }
    }

    this.hp = Math.max(0, this.hp - finalDmg);

    // Start hit invulnerability window
    this.hitInvulnTimer = this.hitInvulnDuration;

    // Flash red
    this.hitFlashTimer = this.hitFlashDuration;
    this.gfx.tint = 0xff0000;

    // Knockback away from damage source
    if (fromX !== undefined && fromY !== undefined) {
      const playerCenterX = this.x + TILE_SIZE / 2;
      const playerCenterY = this.y + TILE_SIZE / 2;
      const dx = playerCenterX - fromX;
      const dy = playerCenterY - fromY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) {
        // 10px knockback impulse
        this.hitKnockbackX = (dx / dist) * 40;
        this.hitKnockbackY = (dy / dist) * 40;
      }
    }

    // Notify listeners (screen shake, HUD flash, etc.)
    if (this.onDamaged) {
      this.onDamaged(finalDmg);
    }
  }

  /**
   * Update the ability readiness indicator dot near the player.
   */
  _updateAbilityIndicator() {
    if (!this.abilityIndicatorGfx) return;

    const half = this.size / 2;
    const cx = TILE_SIZE / 2;
    const cy = TILE_SIZE / 2;

    this.abilityIndicatorGfx.clear();
    if (this.abilityReady) {
      // Green dot: ability ready
      this.abilityIndicatorGfx.circle(cx + half + 2, cy - half - 2, 2);
      this.abilityIndicatorGfx.fill(0x44ff44);
    } else {
      // Red dot: ability on cooldown
      this.abilityIndicatorGfx.circle(cx + half + 2, cy - half - 2, 2);
      this.abilityIndicatorGfx.fill(0xff4444);
    }
    this.abilityIndicatorGfx.visible = true;
  }

  /**
   * Update the shield visual ring around the player (Voidpriest ward).
   */
  _updateShieldVisual() {
    if (!this.shieldGfx) return;

    if (this.shield > 0) {
      this.shieldGfx.clear();
      const cx = TILE_SIZE / 2;
      const cy = TILE_SIZE / 2;
      const radius = this.size / 2 + 3;
      this.shieldGfx.circle(cx, cy, radius);
      this.shieldGfx.stroke({ width: 1.5, color: 0xffcc44 });
      this.shieldGfx.visible = true;
      this.shieldGfx.alpha = 0.5 + Math.sin(Date.now() * 0.005) * 0.3;
    } else {
      this.shieldGfx.visible = false;
    }
  }

  /**
   * Start a player glow effect (used by abilities).
   * @param {number} color - Glow color
   * @param {number} duration - Duration in seconds
   */
  startGlow(color, duration) {
    this.glowColor = color;
    this.glowTimer = duration;
    this.glowGfx.clear();
    const cx = TILE_SIZE / 2;
    const cy = TILE_SIZE / 2;
    this.glowGfx.circle(cx, cy, this.size / 2 + 5);
    this.glowGfx.fill(color);
    this.glowGfx.visible = true;
    this.glowGfx.alpha = 0.3;
  }

  /**
   * Apply movement with axis-separated tile collision (allows wall sliding).
   */
  _applyMovement(moveX, moveY, tiles) {
    // Try X movement
    if (moveX !== 0) {
      const newX = this.x + moveX;
      if (this._canMoveTo(newX, this.y, tiles)) {
        this.x = newX;
      }
    }
    // Try Y movement
    if (moveY !== 0) {
      const newY = this.y + moveY;
      if (this._canMoveTo(this.x, newY, tiles)) {
        this.y = newY;
      }
    }
  }

  /**
   * Check if the player hitbox can occupy position (x, y).
   * Tests all four corners of the player bounding box using this.size.
   */
  _canMoveTo(x, y, tiles) {
    const pad = 1; // 1px inset to prevent snagging
    const offset = (TILE_SIZE - this.size) / 2;
    const left = x + offset + pad;
    const right = x + offset + this.size - pad - 1;
    const top = y + offset + pad;
    const bottom = y + offset + this.size - pad - 1;

    return (
      this._isWalkable(left, top, tiles) &&
      this._isWalkable(right, top, tiles) &&
      this._isWalkable(left, bottom, tiles) &&
      this._isWalkable(right, bottom, tiles)
    );
  }

  /**
   * Check if a pixel position maps to a walkable tile.
   */
  _isWalkable(pixelX, pixelY, tiles) {
    const col = Math.floor(pixelX / TILE_SIZE);
    const row = Math.floor(pixelY / TILE_SIZE);
    const height = tiles.length;
    const width = tiles[0].length;

    if (row < 0 || row >= height || col < 0 || col >= width) {
      return false;
    }

    return WALKABLE_TILES.has(tiles[row][col]);
  }

  /**
   * Sync the PixiJS container position to the entity position.
   */
  _syncVisual() {
    this.container.x = Math.round(this.x);
    this.container.y = Math.round(this.y);
  }
}
